/**
 * TokenPropertiesContent Component - Reusable Token Properties Form
 * 
 * This component contains the actual form content for editing token properties.
 * It can be used both in the popup and in the sidebar when pinned.
 * 
 * Props:
 * - token: The token object with all properties
 * - onUpdate: Function to update token properties
 * - onTokenSizeChange: Function to update token size
 * - showHeader: Whether to show the header with pin/close buttons
 * - onClose: Function to close (for sidebar mode)
 * - isPinned: Whether the component is currently pinned
 * - onTogglePin: Function to toggle pin state
 */

import React, { useState, useEffect, useRef } from 'react';
import EffectColors from '../../config/EffectColors';
import ImageWithFallback from '../ImageWithFallback';
import './TokenPropertiesStyles.css'; // Import new enhanced styles
import TokenPresentationControls from '../battle-room-v2/components/TokenPresentationControls';
import AuthoritativeDamageControls from '../battle-room-v2/components/AuthoritativeDamageControls';
// date_of_change: 2025-09-01 - Removed duplicate MonsterDataValidator, using unified DataService

// @date_of_change: 2025-09-29 - Use centralized ImageWithFallback; DEFAULT_TOKEN_IMAGE removed

const TokenPropertiesContent = ({ 
  token, 
  onUpdate, 
  onTokenSizeChange, 
  showHeader = false,
  onClose,
  isPinned = false,
  onTogglePin,
  isGameMaster = false
}) => {
  // Debug logging to track component usage
  console.debug('[TokenPropertiesContent] Rendering with token:', token?.id || 'none');
  
  // Helper function to calculate ability score modifier
  const getAbilityModifier = (score) => {
    if (!score) return '+0';
    const modifier = Math.floor((score - 10) / 2);
    return modifier >= 0 ? `+${modifier}` : `${modifier}`;
  };
  
  // Consolidated HP update handler to ensure consistent HP format
  const handleHPUpdate = (field, value) => {
    try {
      // Create new form data with current values
      const newFormData = { ...formData };
      
      // Initialize HP structure if not present
      if (!newFormData.hp || typeof newFormData.hp !== 'object') {
        newFormData.hp = { current: 0, max: 1 };
      }
      
      // Initialize hpObj structure if not present
      if (!newFormData.hpObj || typeof newFormData.hpObj !== 'object') {
        newFormData.hpObj = { current: 0, max: 1 };
      }
      
      // Update the nested hp object
      newFormData.hp[field] = value;

      // Update the standard hpObj format simultaneously
      newFormData.hpObj[field] = value;

      // date_of_change: 2026-05-19 - GAP-001: Sync legacy scalar fields WITHOUT overwriting the
      // hp object. Previously newFormData.hp = value replaced the {current,max} object with a
      // scalar, causing downstream readers expecting hp.current to get undefined.
      // rule_reference: HPFormatBridge expects hpObj.current / hpObj.max as the canonical format.
      if (field === 'current') {
        newFormData.currentHp = value; // legacy scalar alias only — hp object stays intact
      } else if (field === 'max') {
        newFormData.maxHp = value; // Legacy maxHp field
      }
      
      // Update form state
      setFormData(newFormData);
      
      // Log the update for debugging
      console.debug(`[TokenPropertiesContent] Updated HP ${field}:`, {
        value,
        hp: newFormData.hp,
        hpObj: newFormData.hpObj,
        legacyHP: newFormData.hp,
        legacyMaxHP: newFormData.maxHp
      });
      
      // Call onUpdate with standardized format
      if (onUpdate) {
        const updateData = { ...newFormData };
        onUpdate(updateData);
      }
    } catch (error) {
      console.error(`[TokenPropertiesContent] Error updating HP ${field}:`, error);
    }
  };
  
  // Form data state
  const [formData, setFormData] = useState({
    name: '',
    type: 'player',
    hp: { current: 0, max: 0 },
    ac: 0,
    initiative: 0,
    speed: 30,
    level: 1,
  });

  const handleRemoveEffect = (key) => {
    if (!isGameMaster) return;
    try {
      const currentSE = { ...(formData?.statusEffects || {}) };
      const currentMeta = { ...(formData?.statusEffectsMeta || {}) };
      if (currentSE[key]) delete currentSE[key];
      if (currentMeta[key]) delete currentMeta[key];
      const next = { ...formData, statusEffects: currentSE, statusEffectsMeta: currentMeta };
      setFormData(next);
      if (onUpdate) onUpdate({ statusEffects: currentSE, statusEffectsMeta: currentMeta, _gmOverride: true });
    } catch (_) {}
  };
  
  // File input reference for the image upload button
  const fileInputRef = useRef(null);
  
  // Upload progress state for visual feedback
  const [uploadProgress, setUploadProgress] = useState(0);

  // Update form data when token changes
  // @date_of_change 2025-08-27 Enhanced for PHASE 4b to prioritize token.monsterData
  useEffect(() => {
    if (token) {
      // 🔍 PHASE 4b: TokenPropertiesContent Enhanced Data Access
      console.log('🔍 DEBUG - TokenPropertiesContent raw token data:', {
        name: token.name,
        type: token.type,
        hasMonsterData: !!token.monsterData,
        hasDirectActions: Array.isArray(token.actions),
        directActionsCount: Array.isArray(token.actions) ? token.actions.length : 0,
        monsterDataActionsCount: token.monsterData?.actions?.length || 0,
        tokenKeys: Object.keys(token)
      });
      
      try {
        // Create variables to store normalized data
        let normalizedToken = {...token};
        let normalizedActions = [];
        let normalizedLegendaryActions = [];
        let sourceData;
        let dataSource = 'unknown';
        
        // PHASE 4b: Prioritize complete monster data sources for monster tokens
        if (token.type === 'monster') {
          if (token.monsterData) {
            // First priority: Use complete monster data preserved from dashboard (no full normalization)
            sourceData = token.monsterData;
            dataSource = 'token.monsterData';
            console.log('🔍 DEBUG - TokenPropertiesContent using complete monster data from token.monsterData (no transformation)');

            normalizedToken = {
              ...token,
              ...sourceData,
              // Preserve token-specific properties
              id: token.id,
              x: token.x,
              y: token.y,
              src: token.src,
              artwork: token.artwork
            };
          } else {
            // Fallback to identity mapping of the token (no full normalization)
            sourceData = token;
            dataSource = 'token_direct';
            console.log('🔍 DEBUG - TokenPropertiesContent using direct token (no transformation)');
            normalizedToken = { ...token };
          }
          
          // date_of_change: 2025-09-01 - Simplified validation using unified system
          console.log('[TokenPropertiesContent] Monster data loaded:', sourceData?.name || 'unnamed');
          if (!sourceData?.name) {
            console.warn('[TokenPropertiesContent] Monster missing name field');
          }
          
          console.log('🔍 DEBUG - TokenPropertiesContent monster data selected:', {
            dataSource,
            name: normalizedToken.name,
            hasActions: Array.isArray(normalizedToken.actions),
            actionCount: Array.isArray(normalizedToken.actions) ? normalizedToken.actions.length : 0,
            hasName: !!sourceData?.name
          });
          
          console.debug('[TokenPropertiesContent] Monster data (identity-mapped)', normalizedToken);
        } else {
          // For non-monster tokens, normalize stats and abilities with defaults
          const safeStats = { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10, ...token.stats };
          const safeAbilities = { strength: 10, dexterity: 10, constitution: 10, intelligence: 10, wisdom: 10, charisma: 10, ...token.abilities };
          
          normalizedToken = {
            ...token,
            stats: safeStats,
            abilities: safeAbilities
          };
          
          console.log('🔍 DEBUG - TokenPropertiesContent normalized non-monster token');
        }
        
        // PHASE 4b: Enhanced action processing with data preservation
        if (Array.isArray(normalizedToken.actions)) {
          normalizedActions = normalizedToken.actions.map(action => ({
            ...action,
            name: action.name || 'Unnamed Action',
            description: action.description || action.desc || action.text || '',
            desc: action.description || action.desc || action.text || '',
            text: action.description || action.desc || action.text || '',
            // Preserve additional action data
            damage: action.damage || action.damageRoll || '',
            attackBonus: action.attackBonus || action.bonus || 0
          }));
          
          console.log('🔍 DEBUG - TokenPropertiesContent processed actions:', {
            actionsCount: normalizedActions.length,
            sampleAction: normalizedActions[0]?.name || 'none'
          });
        }

        // Process legendary actions with enhanced data preservation
        if (Array.isArray(normalizedToken.legendaryActions)) {
          normalizedLegendaryActions = normalizedToken.legendaryActions.map(action => ({
            ...action,
            name: action.name || 'Unnamed Legendary Action',
            description: action.description || action.desc || action.text || '',
            desc: action.description || action.desc || action.text || '',
            text: action.description || action.desc || action.text || ''
          }));
        } else if (normalizedToken.legendaryActions && typeof normalizedToken.legendaryActions === 'object') {
          // Handle case where legendaryActions might be an object instead of array
          normalizedLegendaryActions = Object.values(normalizedToken.legendaryActions).map(action => ({
            ...action,
            name: action.name || 'Unnamed Legendary Action',
            description: action.description || action.desc || action.text || '',
            desc: action.description || action.desc || action.text || '',
            text: action.description || action.desc || action.text || ''
          }));
        }
        
        console.debug('[TokenPropertiesContent] Normalized actions:', normalizedActions);
        console.debug('[TokenPropertiesContent] Normalized legendary actions:', normalizedLegendaryActions);
        
        // date_of_change: 2025-09-01 - Simplified final report using unified system
        console.log('[TokenPropertiesContent] Token ready for display:', { 
          name: normalizedToken?.name, 
          actions: normalizedActions?.length || 0,
          legendaryActions: normalizedLegendaryActions?.length || 0
        });
        
        // Extract ability scores and stats safely with enhanced logging
        const abilities = normalizedToken.abilities || {};
        const stats = normalizedToken.stats || {};
        
        // Use stats first, fallback to abilities for D&D 5e compatibility
        const strength = stats.str || abilities.strength || 10;
        const dexterity = stats.dex || abilities.dexterity || 10;
        const constitution = stats.con || abilities.constitution || 10;
        const intelligence = stats.int || abilities.intelligence || 10;
        const wisdom = stats.wis || abilities.wisdom || 10;
        const charisma = stats.cha || abilities.charisma || 10;
        
        console.log('🔍 DEBUG - TokenPropertiesContent extracted abilities:', {
          strength, dexterity, constitution, intelligence, wisdom, charisma,
          hasStats: Object.keys(stats).length > 0,
          hasAbilities: Object.keys(abilities).length > 0
        });
        
        // Normalize hpObj and legacy hp fields
        const validatedHpObj = normalizedToken.hpObj || { 
          current: parseInt(normalizedToken.hp) || 0, 
          max: parseInt(normalizedToken.maxHp) || parseInt(normalizedToken.hp) || 0 
        };
        
        // Set form data with fully normalized token data
        const newFormData = { 
          ...normalizedToken,
          actions: normalizedActions,
          legendaryActions: normalizedLegendaryActions,
          hpObj: validatedHpObj,
          hp: validatedHpObj // Set hp as hpObj for component internal use
        };
        
        console.debug('[TokenPropertiesContent] Form data updated:', {
          name: normalizedToken.name,
          type: normalizedToken.type,
          hp: newFormData.hp,
          ac: normalizedToken.ac,
          actions: normalizedActions.length,
          legendaryActions: normalizedLegendaryActions.length,
          dataSource,
          completeness: {
            hasCompleteData: normalizedActions.length > 0,
            preservedFromDashboard: dataSource === 'token.monsterData'
          }
        });
        
        setFormData(newFormData);
        
        // Debug what we're setting as form data
        console.debug('[TokenPropertiesContent] Form data set successfully');
      } catch (error) {
        console.error('[TokenPropertiesContent] Error processing token data:', error);
        
        // Create minimal default form data on error
        setFormData({
          name: token.name || 'Error Loading Token',
          type: token.type || 'player',
          hp: { current: 0, max: 1 },
          hpObj: { current: 0, max: 1 },
          stats: { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 },
          abilities: {
            strength: 10, dexterity: 10, constitution: 10,
            intelligence: 10, wisdom: 10, charisma: 10
          },
          ac: 10,
          initiative: 0,
          speed: 30
        });
      }
    }
  }, [token]);

  // Handle image upload from file input
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      console.error('[TokenPropertiesContent] Invalid file type:', file.type);
      alert('Please select an image file');
      return;
    }
    
    console.log('[TokenPropertiesContent] Processing image upload:', file.name);
    
    // Start progress simulation
    setUploadProgress(10);
    
    const reader = new FileReader();
    
    reader.onprogress = (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        setUploadProgress(Math.min(90, progress)); // Cap at 90% until fully processed
      }
    };
    
    reader.onload = (e) => {
      try {
        const dataUrl = e.target.result;
        console.log(`[TokenPropertiesContent] Image read successfully (${dataUrl.length} chars)`);
        
        // Create image to get dimensions
        const img = new Image();
        img.onload = () => {
          const width = img.width;
          const height = img.height;
          
          // Update form data with new image source
          const updatedFormData = {
            ...formData,
            src: dataUrl,
            width: width,
            height: height
          };
          
          setFormData(updatedFormData);
          setUploadProgress(100);
          
          // Auto-save changes
          if (onUpdate) {
            console.log('[TokenPropertiesContent] Updating token with new image');
            onUpdate(updatedFormData);
          }
          
          // Clear progress after a delay
          setTimeout(() => setUploadProgress(0), 2000);
        };
        
        img.onerror = () => {
          console.error('[TokenPropertiesContent] Failed to process image');
          setUploadProgress(0);
          alert('Failed to process image. Please try again.');
        };
        
        img.src = dataUrl;
      } catch (error) {
        console.error('[TokenPropertiesContent] Error processing image upload:', error);
        setUploadProgress(0);
        alert('Failed to upload image. Please try again.');
      }
    };
    
    reader.onerror = () => {
      console.error('[TokenPropertiesContent] FileReader error in image upload');
      setUploadProgress(0);
      alert('Failed to read image file. Please try again.');
    };
    
    reader.readAsDataURL(file);
  };

  const handleNestedInputChange = (fieldPath, value) => {
    try {
      const newFormData = { ...formData };
      
      // Split field path by dots
      const parts = fieldPath.split('.');
      let current = newFormData;
      
      // Traverse the object until we reach the last part
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) {
          current[parts[i]] = {};
        }
        current = current[parts[i]];
      }
      
      // Set the value
      current[parts[parts.length - 1]] = value;
      
      // Log the update to help debug data flow issues
      console.debug(`[TokenPropertiesContent] Updated nested field: ${fieldPath}`, { value, newData: newFormData });
      
      setFormData(newFormData);
      
      // Auto-save changes with proper hpObj format
      if (onUpdate) {
        try {
          const updateData = { ...newFormData };
          
          // Ensure hpObj format is maintained and legacy fields are synced
          if (updateData.hp && typeof updateData.hp === 'object') {
            // Set both formats for maximum compatibility
            updateData.hpObj = { 
              current: parseInt(updateData.hp.current) || 0, 
              max: parseInt(updateData.hp.max) || 1 
            };
            updateData.hp = parseInt(updateData.hp.current) || 0; // Legacy field for backward compatibility
            updateData.maxHp = parseInt(updateData.hpObj.max) || 1; // Legacy field for backward compatibility
          }
          
          // Log what's being updated for debugging
          console.debug('[TokenPropertiesContent] Updating token with data:', updateData);
          
          onUpdate(updateData);
          
          // Log successful update
          console.debug(`[TokenPropertiesContent] Successfully updated nested field: ${fieldPath}`);
        } catch (updateError) {
          console.error('[TokenPropertiesContent] Error updating token:', updateError);
          // Continue without crashing the component
        }
      }
    } catch (error) {
      console.error(`[TokenPropertiesContent] Error processing nested field ${fieldPath}:`, error);
      // Continue without crashing the component
    }
  };

  const handleInputChange = (field, value) => {
    try {
      const newFormData = { ...formData };
      
      if (field.includes('.')) {
        // Use the dedicated function for nested field updates
        handleNestedInputChange(field, value);
        return;
      } else {
        // Direct field update
        newFormData[field] = value;
        
        // Special handling for ability scores to ensure consistent format
        // Check if field is an ability score (either short or long name)
        const abilityScoreFields = {
          'strength': 'str', 'dexterity': 'dex', 'constitution': 'con',
          'intelligence': 'int', 'wisdom': 'wis', 'charisma': 'cha',
          'str': 'strength', 'dex': 'dexterity', 'con': 'constitution',
          'int': 'intelligence', 'wis': 'wisdom', 'cha': 'charisma'
        };
        
        if (field in abilityScoreFields) {
          // Get the corresponding short/long name
          const altField = abilityScoreFields[field];
          const isShortName = field.length <= 3;
          const longName = isShortName ? altField : field;
          const shortName = isShortName ? field : altField;
          
          // Update all formats simultaneously to ensure consistency
          
          // 1. Update abilities object (preferred format)
          if (!newFormData.abilities) newFormData.abilities = {};
          newFormData.abilities[longName] = value;
          
          // 2. Update stats object (backup format)
          if (!newFormData.stats) newFormData.stats = {};
          newFormData.stats[shortName] = value;
          
          // 3. Update direct properties (legacy format)
          newFormData[longName] = value;
          newFormData[shortName] = value;
          
          console.debug(`[TokenPropertiesContent] Synchronized ability score formats for ${field}`, {
            longName, shortName, value
          });
        }
        
        // Log update for debugging
        console.debug(`[TokenPropertiesContent] Updated field: ${field}`, { value, newData: newFormData });
        
        setFormData(newFormData);
        
        // Auto-save changes with proper hpObj format
        if (onUpdate) {
          try {
            const updateData = { ...newFormData };
            
            // Ensure hpObj format is maintained and legacy fields are synced
            if (updateData.hp && typeof updateData.hp === 'object') {
              // Set both formats for maximum compatibility
              updateData.hpObj = { 
                current: parseInt(updateData.hp.current) || 0, 
                max: parseInt(updateData.hp.max) || 1 
              };
              updateData.hp = parseInt(updateData.hp.current) || 0; // Legacy field for backward compatibility
              updateData.maxHp = parseInt(updateData.hpObj.max) || 1; // Legacy field for backward compatibility
            }
            
            // Log what's being updated for debugging
            console.debug('[TokenPropertiesContent] Updating token with data:', updateData);
            
            onUpdate(updateData);
            
            // Log successful update
            console.debug('[TokenPropertiesContent] Successfully updated token field:', field);
          } catch (updateError) {
            console.error('[TokenPropertiesContent] Error updating token:', updateError);
            // Continue without crashing the component
          }
        }
      }
    } catch (error) {
      console.error(`[TokenPropertiesContent] Error processing field ${field}:`, error);
      // Continue without crashing the component
    }
  };

  if (!token) {
    return (
      <div className="no-token-content">
        <p>No token selected</p>
        <small>Select a token to view its properties</small>
        <div className="actions">
          <button 
            className={`popup-pin ${isPinned ? 'pinned' : ''}`} 
            onClick={onTogglePin}
            title={isPinned ? 'Unpin to popup' : 'Pin to sidebar'}
          >
            {isPinned ? '📍' : '📌'}
          </button>
          <button 
            className="popup-close" 
            onClick={onClose}
            title="Close"
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`token-properties-content ${isPinned ? 'pinned' : ''}`}>
      <div className="properties-grid">
        <div className="property-section">
          <h4>Active Effects</h4>
          {(() => {
            const se = (formData && formData.statusEffects) ? formData.statusEffects : {};
            const seKeys = Object.keys(se).filter(k => !!se[k]);
            const conditions = Array.isArray(formData?.conditions) ? formData.conditions : [];
            const palette = EffectColors || {}; const table = palette || {}; const map = palette.byEffect || {};
            if (seKeys.length === 0 && conditions.length === 0) return (<div className="effects-empty">None</div>);
            return (
              <div className="effects-list">
                {seKeys.map(key => {
                  const colorKey = map[key] || key; const color = table[colorKey] || '#4FC3F7';
                  const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                  return (
                    <span key={`se-${key}`} className="effect-pill" title={label}>
                      <span className="effect-dot" style={{ backgroundColor: color }} />
                      {label}
                      {isGameMaster && (
                        <button
                          className="effect-remove"
                          style={{ marginLeft: 6, cursor: 'pointer', background: 'transparent', border: 'none' }}
                          onClick={(e) => { e.stopPropagation(); handleRemoveEffect(key); }}
                          title={`Remove ${label}`}
                        >
                          ×
                        </button>
                      )}
                    </span>
                  );
                })}
                {conditions.length > 0 && (
                  <div className="conditions-block">
                    <div className="conditions-title">Conditions:</div>
                    <div className="conditions-pills">
                      {conditions.map(c => (
                        <span key={`cond-${c}`} className="effect-pill neutral" title={c}>
                          {String(c).replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase())}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        <TokenPresentationControls token={token} />
        <AuthoritativeDamageControls token={token} isGameMaster={isGameMaster} />

        <div className="property-section">
          <h4>Basic Information</h4>
          <div className="property-row">
            <label>Name:</label>
            <input
              type="text"
              value={formData.name || ''}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Token name"
            />
          </div>
          <div className="property-row">
            <label>Type:</label>
            <select
              value={formData.type || 'player'}
              onChange={(e) => handleInputChange('type', e.target.value)}
            >
              <option value="player">Player</option>
              <option value="monster">Monster</option>
              <option value="npc">NPC</option>
              <option value="map">Map Asset</option>
              <option value="object">Object</option>
            </select>
            {formData.originalType && formData.type === 'map' && (
              <div className="token-metadata">
                <small>Original type: {formData.originalType}</small>
                <small>(Will be restored when moved off map layer)</small>
              </div>
            )}
          </div>
          <div className="property-row">
            <label>Size:</label>
            <select
              value={formData.size || 'Medium'}
              onChange={(e) => handleInputChange('size', e.target.value)}
            >
              <option value="Tiny">Tiny</option>
              <option value="Small">Small</option>
              <option value="Medium">Medium</option>
              <option value="Large">Large</option>
              <option value="Huge">Huge</option>
              <option value="Gargantuan">Gargantuan</option>
            </select>
          </div>
          <div className="property-row">
            <label>Grid Position:</label>
            <div className="grid-coordinates">
              <span>({formData.gridX || 0}, {formData.gridY || 0})</span>
              <small>Grid coordinates (0,0 = upper left)</small>
            </div>
          </div>
        </div>

        {/* Map Properties - Only shown for map assets */}
        {(formData.type === 'map' || formData.type === 'object') && (
          <div className="property-section">
            <h4>Map/Object Properties</h4>
            
            <div className="property-row">
              <label>Z-Index:</label>
              <input
                type="number"
                value={formData.zIndex || 0}
                onChange={(e) => handleInputChange('zIndex', parseInt(e.target.value) || 0)}
                min="0"
              />
            </div>
            
            <div className="property-row">
              <label>Description:</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Map description or notes"
                rows="2"
              />
            </div>
          </div>
        )}

        {/* Combat Stats - Only shown for character entities */}
        {(formData.type === 'player' || formData.type === 'monster' || formData.type === 'npc' || 
          (formData.originalType && ['player', 'monster', 'npc'].includes(formData.originalType))) && (
          <div className="property-section">
            <h4>Combat Stats</h4>
            
            <div className="property-row hp-row">
              <label>Hit Points:</label>
              <div className="hp-section">
                <div className="hp-display">
                  <span className="hp-current">{formData.hp?.current || formData.hpObj?.current || 0}</span>
                  <span className="hp-separator">/</span>
                  <span className="hp-max">{formData.hp?.max || formData.hpObj?.max || 0}</span>
                </div>
                <div className="hp-inputs">
                  <input
                    type="number"
                    value={formData.hp?.current || formData.hpObj?.current || 0}
                    onChange={(e) => handleHPUpdate('current', parseInt(e.target.value) || 0)}
                    placeholder="Current"
                    min="0"
                    aria-label="Current HP"
                  />
                  <span className="hp-separator">/</span>
                  <input
                    type="number"
                    value={formData.hp?.max || formData.hpObj?.max || 0}
                    onChange={(e) => handleHPUpdate('max', parseInt(e.target.value) || 0)}
                    placeholder="Max"
                    min="1"
                    aria-label="Maximum HP"
                  />
                </div>
              </div>
            </div>
            
            <div className="property-row">
              <label>Armor Class:</label>
              <input
                type="number"
                value={formData.ac || 0}
                onChange={(e) => handleInputChange('ac', parseInt(e.target.value) || 0)}
                min="0"
                max="30"
              />
            </div>
            
            <div className="property-row">
              <label>Initiative:</label>
              <input
                type="number"
                value={formData.initiative || 0}
                onChange={(e) => handleInputChange('initiative', parseInt(e.target.value) || 0)}
                min="-10"
                max="30"
              />
            </div>
            
            <div className="property-row">
              <label>Speed:</label>
              <input
                type="number"
                value={formData.speed || 30}
                onChange={(e) => handleInputChange('speed', parseInt(e.target.value) || 30)}
                min="0"
                max="120"
              />
              <small>feet</small>
            </div>
          </div>
        )}

        {/* Character Stats - Only shown for character entities */}
        {(formData.type === 'player' || formData.type === 'monster' || formData.type === 'npc' || 
          (formData.originalType && ['player', 'monster', 'npc'].includes(formData.originalType))) && (
          <div className="property-section">
            <h4>Ability Scores</h4>
            
            <div className="ability-grid">
              {[
                { name: 'STR', full: 'strength', short: 'str' },
                { name: 'DEX', full: 'dexterity', short: 'dex' },
                { name: 'CON', full: 'constitution', short: 'con' },
                { name: 'INT', full: 'intelligence', short: 'int' },
                { name: 'WIS', full: 'wisdom', short: 'wis' },
                { name: 'CHA', full: 'charisma', short: 'cha' }
              ].map(ability => {
                // Check all possible paths for ability scores in proper priority order
                // Safe access to ability scores with error handling
                let abilityScore;
                try {
                  abilityScore = 
                    // 1. Check abilities object (preferred from MonsterDataNormalizer)
                    (formData.abilities && formData.abilities[ability.full]) ||
                    // 2. Check stats object (MonsterDataNormalizer backup format)
                    (formData.stats && formData.stats[ability.short]) ||
                    // 3. Check direct properties (legacy format)
                    formData[ability.full] || 
                    formData[ability.short] || 
                    formData[ability.name.toLowerCase()];
                    
                  // Validate it's a proper number in the valid range
                  abilityScore = typeof abilityScore === 'number' && !isNaN(abilityScore) ? 
                    Math.min(Math.max(abilityScore, 1), 30) : 10;
                } catch (error) {
                  console.error(`[TokenPropertiesContent] Error accessing ${ability.name} score:`, error);
                  abilityScore = 10; // Safe default
                }
                
                const modifier = getAbilityModifier(abilityScore);
                return (
                  <div key={ability.full} className="ability-score">
                    <label>{ability.name}</label>
                    <div className="ability-value">
                      <input
                        type="number"
                        value={abilityScore}
                        onChange={(e) => {
                          const newValue = parseInt(e.target.value) || 10;
                          
                          // Create a batch update for all ability scores
                          const newFormData = { ...formData };
                          
                          // Update all formats at once for consistency
                          
                          // 1. Update in abilities object (preferred format)
                          if (!newFormData.abilities) {
                            newFormData.abilities = {};
                          }
                          newFormData.abilities[ability.full] = newValue;
                          
                          // 2. Update in stats object (backup format)
                          if (!newFormData.stats) {
                            newFormData.stats = {};
                          }
                          newFormData.stats[ability.short] = newValue;
                          
                          // 3. Update direct property (legacy format)
                          newFormData[ability.full] = newValue;
                          newFormData[ability.short] = newValue;
                          
                          // Apply the complete update at once
                          setFormData(newFormData);
                          console.debug(`[TokenPropertiesContent] Updated ability score: ${ability.name}=${newValue}`);
                          
                          // Send a single update to the parent with all formats
                          if (onUpdate) {
                            onUpdate(newFormData);
                          }
                        }}
                        min="1"
                        max="30"
                      />
                      <div className="ability-modifier">{modifier}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Actions - Only shown for monsters */}
        {(formData.type === 'monster' || 
          (formData.originalType === 'monster')) && (
          <div className="property-section actions-section">
            <h4>Actions</h4>
            {formData.actions && formData.actions.length > 0 ? (
              <div className="actions-list">
                {formData.actions.map((action, index) => (
                  <div key={`action-${index}`} className="action-item">
                    <div className="action-name">
                      <input
                        type="text"
                        value={action.name || ''}
                        onChange={(e) => {
                          // Create a deep copy of actions to avoid direct state mutation
                          const updatedActions = [...formData.actions];
                          updatedActions[index] = {
                            ...updatedActions[index],
                            name: e.target.value
                          };
                          handleInputChange('actions', updatedActions);
                        }}
                      />
                    </div>
                    <div className="action-description">
                      <textarea
                        value={action.description || action.desc || action.text || ''}
                        onChange={(e) => {
                          // Create a deep copy of actions to avoid direct state mutation
                          const updatedActions = [...formData.actions];
                          const descriptionValue = e.target.value;
                          
                          // Update all possible description field formats for maximum compatibility
                          updatedActions[index] = {
                            ...updatedActions[index],
                            description: descriptionValue,
                            desc: descriptionValue, // Legacy format
                            text: descriptionValue  // Legacy format
                          };
                          
                          handleInputChange('actions', updatedActions);
                        }}
                        rows={3}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-actions">No actions available</div>
            )}
          </div>
        )}
        
        {/* Legendary Actions - Only shown for monsters with legendary actions */}
        {(formData.type === 'monster' || formData.originalType === 'monster') && 
          formData.legendaryActions && formData.legendaryActions.length > 0 && (
          <div className="property-section actions-section">
            <h4>Legendary Actions</h4>
            <div className="actions-list">
              {formData.legendaryActions.map((action, index) => (
                <div key={`legendary-${index}`} className="action-item">
                  <div className="action-name">
                    <input
                      type="text"
                      value={action.name || ''}
                      onChange={(e) => {
                        // Create a deep copy of legendary actions to avoid direct state mutation
                        const updatedActions = [...formData.legendaryActions];
                        updatedActions[index] = {
                          ...updatedActions[index],
                          name: e.target.value
                        };
                        handleInputChange('legendaryActions', updatedActions);
                      }}
                    />
                  </div>
                  <div className="action-description">
                    <textarea
                      value={action.description || action.desc || action.text || ''}
                      onChange={(e) => {
                        // Create a deep copy of legendary actions to avoid direct state mutation
                        const updatedActions = [...formData.legendaryActions];
                        const descriptionValue = e.target.value;
                        
                        // Update all possible description field formats for maximum compatibility
                        updatedActions[index] = {
                          ...updatedActions[index],
                          description: descriptionValue,
                          desc: descriptionValue, // Legacy format
                          text: descriptionValue  // Legacy format
                        };
                        
                        handleInputChange('legendaryActions', updatedActions);
                      }}
                      rows={3}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Token Image Preview Section */}
        <div className="property-section image-preview-section">
          <h4>Token Image</h4>
          <div className="token-image-container">
            {formData.src ? (
              <>
                <ImageWithFallback
                  src={formData.src}
                  alt={formData.name || 'Token'}
                  className="token-image-preview"
                />
                <div className="image-metadata">
                  <div className="image-size">
                    {formData.width}×{formData.height} pixels
                  </div>
                  {formData.type && (
                    <div className="token-type">
                      Type: {formData.type.charAt(0).toUpperCase() + formData.type.slice(1)}
                    </div>
                  )}
                </div>
                
                <div className="image-upload-controls">
                  <input
                    type="file"
                    id="token-image-upload"
                    accept="image/*"
                    className="image-upload-input"
                    onChange={handleImageUpload}
                    ref={fileInputRef}
                  />
                  <button 
                    className="image-upload-button" 
                    onClick={() => fileInputRef.current.click()}
                    title="Replace token image"
                  >
                    Replace Image
                  </button>
                  {uploadProgress && (
                    <div className="upload-progress">
                      <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                      <span>{uploadProgress}%</span>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="no-image-placeholder">
                <span>No image available</span>
                <button 
                  className="image-upload-button" 
                  onClick={() => fileInputRef.current.click()}
                  title="Add token image"
                >
                  Add Image
                </button>
                <input
                  type="file"
                  id="token-image-upload"
                  accept="image/*"
                  className="image-upload-input"
                  onChange={handleImageUpload}
                  ref={fileInputRef}
                />
              </div>
            )}
          </div>
        </div>

        {/* Notes Section */}
        <div className="property-section">
          <h4>Notes</h4>
          <textarea
            className="notes-input"
            value={formData.notes || ''}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Add notes about this token..."
            rows="3"
          />
        </div>
      </div>
    </div>
  );
};

export default TokenPropertiesContent;
